import { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eraser, Save, Maximize2, Minimize2 } from 'lucide-react';

export function SignatureCanvas({ onSave, onCancel, fullscreen: initialFullscreen = false }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [nomSignataire, setNomSignataire] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(initialFullscreen);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => { setupCanvas(); }, [setupCanvas]);

  // Resize canvas when going fullscreen
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    if (isFullscreen) {
      canvas.width = Math.min(window.innerWidth - 80, 1400);
      canvas.height = Math.min(window.innerHeight - 250, 500);
    } else {
      canvas.width = 600;
      canvas.height = 200;
    }

    ctx.putImageData(imageData, 0, 0);
    setupCanvas();
  }, [isFullscreen, setupCanvas]);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing, getPos]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const prevent = (e) => { if (isDrawing) e.preventDefault(); };
    canvas.addEventListener('touchmove', prevent, { passive: false });
    return () => canvas.removeEventListener('touchmove', prevent);
  }, [isDrawing]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSave = () => {
    if (!nomSignataire.trim()) {
      alert('Veuillez entrer votre nom');
      return;
    }
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    onSave(signatureData, nomSignataire);
  };

  // Fullscreen overlay
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white flex flex-col" ref={containerRef}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <h2 className="text-xl font-bold">Signature</h2>
          <Button variant="outline" size="sm" onClick={() => setIsFullscreen(false)}>
            <Minimize2 className="w-4 h-4 mr-1" /> Réduire
          </Button>
        </div>

        <div className="px-6 py-3">
          <Label htmlFor="nom_signataire_fs">Nom du signataire *</Label>
          <Input
            id="nom_signataire_fs"
            placeholder="Entrez votre nom complet"
            value={nomSignataire}
            onChange={(e) => setNomSignataire(e.target.value)}
            className="max-w-md"
          />
        </div>

        <div className="flex-1 px-6 pb-3">
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none h-full flex items-center justify-center">
            <canvas
              ref={canvasRef}
              width={1400}
              height={500}
              className="cursor-crosshair w-full h-full touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        </div>

        <div className="flex justify-between px-6 py-4 border-t bg-gray-50">
          <Button variant="outline" onClick={clearCanvas}>
            <Eraser className="w-4 h-4 mr-2" /> Effacer
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>Annuler</Button>
            <Button onClick={handleSave}>
              <Save className="w-4 h-4 mr-2" /> Sauvegarder
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Normal (inline) mode
  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-end justify-between gap-4">
          <div className="flex-1">
            <Label htmlFor="nom_signataire">Nom du signataire *</Label>
            <Input
              id="nom_signataire"
              placeholder="Entrez votre nom complet"
              value={nomSignataire}
              onChange={(e) => setNomSignataire(e.target.value)}
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => setIsFullscreen(true)} title="Agrandir" data-testid="fullscreen-signature-btn">
            <Maximize2 className="w-4 h-4 mr-1" /> Agrandir
          </Button>
        </div>

        <div>
          <Label>Signature</Label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="cursor-crosshair w-full touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">Signez avec votre doigt ou votre souris</p>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={clearCanvas}>
            <Eraser className="w-4 h-4 mr-2" /> Effacer
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>Annuler</Button>
            <Button onClick={handleSave}>
              <Save className="w-4 h-4 mr-2" /> Sauvegarder
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
